import { Injectable, Inject, PLATFORM_ID, signal } from '@angular/core';
import { Empty } from '@ngx-grpc/well-known-types';
import { GrpcMetadata, GrpcStatusEvent } from '@ngx-grpc/common';
import { Router } from '@angular/router';
import { StorageService } from '../../services/storage.service';
import { LoadingService } from './loading.service';
import { finalize, firstValueFrom } from 'rxjs';
import { LoginRequest } from '../../protos/generated/admin.pb';
import { IdentityEndpointClient } from '../../protos/generated/admin.pbsc';
import { MatSnackBar } from '@angular/material/snack-bar';

@Injectable({
    providedIn: 'root'
})
export class AuthService {

    private readonly allowedAdminUserGroups = new Set([
        'Platform-Admin',
        'Platform-Business',
        'SystemAdmin'
    ]);

    // Platform-Business without an administrator group is a B2B customer.
    // Keep this reactive so the sidebar updates after user details arrive.
    readonly userGroups = signal<string[] | null>(null);

    isB2bCustomer(): boolean {
        const groups = this.userGroups() ?? this.readStoredUserGroups();
        return groups.includes('Platform-Business') &&
            !groups.some(group => group === 'Platform-Admin' || group === 'SystemAdmin');
    }

    private readStoredUserGroups(): string[] {
        try {
            const value = JSON.parse(this.storageService.getItem('userGroups') || '[]');
            return Array.isArray(value) ? value.filter((group): group is string => typeof group === 'string') : [];
        } catch {
            return [];
        }
    }

    errorMessage = signal('');
    loginLoading = signal(false);
    currentUserInfo: any = null;
    private readonly logoutReasonKey = 'auth_logout_reason';

    constructor(
        private userEndpoint: IdentityEndpointClient,
        private router: Router,
        private storageService: StorageService,
        private loadingService: LoadingService,
        private snackBar: MatSnackBar,
        @Inject(PLATFORM_ID) private platformId: Object
    ) { }

    private userInfoRequest: Promise<boolean> | null = null;

    // Wait for verified groups before making authorization decisions.
    // A failed or incomplete profile must never grant access.
    ensureUserGroupsLoaded(): Promise<boolean> {
        if (!this.getToken() || this.hasTokenExpired()) {
            return Promise.resolve(false);
        }
        if (this.currentUserInfo && this.userGroups() !== null) {
            return Promise.resolve(true);
        }
        if (this.userInfoRequest) {
            return this.userInfoRequest;
        }

        const metadata = this.getMetadata();
        this.userInfoRequest = (async () => {
            try {
                const response = await firstValueFrom(
                    this.userEndpoint.getCurrentUserInfo(new Empty(), metadata)
                );
                // Token may have changed while the request was running.
                if (!this.getToken() || this.hasTokenExpired()) return false;

                const groupTitles = Array.isArray(response.userGroups)
                    ? response.userGroups
                        .map((group: { title?: string }) => group?.title?.trim())
                        .filter((title: unknown): title is string => typeof title === 'string' && title.length > 0)
                    : [];

                this.currentUserInfo = response;
                this.userGroups.set(groupTitles);
                this.storageService.setItem('username', response.username);
                this.storageService.setItem('roles', JSON.stringify(response.roles));
                this.storageService.setItem('userGroups', JSON.stringify(groupTitles));
                return this.hasAdminAccess();
            } catch {
                return false;
            } finally {
                this.userInfoRequest = null;
            }
        })();
        return this.userInfoRequest;
    }

    getCurrentUserInfo(redirectIfAdmin: boolean = false) {
        this.loadingService.setLoading(true);
        void this.ensureUserGroupsLoaded().then(allowed => {
            if (!allowed) {
                this.logoutWithReason('Failed to load or authorize user information. Please sign in again.');
            } else if (redirectIfAdmin) {
                this.router.navigate(['/']);
            }
        }).finally(() => this.loadingService.setLoading(false));
    }

    login(request: LoginRequest) {
        this.loginLoading.set(true);
        this.errorMessage.set('');

        this.userEndpoint.login(request)
            .pipe(finalize(() => this.loginLoading.set(false)))
            .subscribe({
                next: (response) => {
                    this.currentUserInfo = null;
                    this.userGroups.set(null);
                    this.storageService.removeItem('userGroups');
                    this.storageService.setItem('token', response.token);

                    const expireDate = this.calculateExpireTime(response.expiresIn).toString();
                    this.storageService.setItem('expiresIn', expireDate);

                    this.getCurrentUserInfo(true);
                },
                error: (error: GrpcStatusEvent) => {
                    this.errorMessage.set(this.getLoginErrorMessage(error));
                }
            });
    }

    logOut() {
        this.storageService.clear();
        localStorage.clear();
        this.router.navigate(['/authentication']);
    }

    getToken(): string | null {
        return this.storageService.getItem('token');
    }

    hasAccess(role: string[]): boolean {
        const userRoles = JSON.parse(
            this.storageService.getItem('roles') || '[]'
        );
        return role.some(role => userRoles.includes(role));
    }

    private calculateExpireTime(expireTime: number) {
        const expiresIn = new Date().getTime() + expireTime * 1000;
        return expiresIn;
    }

    hasTokenExpired() {
        const expiredTime = localStorage.getItem('expiresIn');
        if (!expiredTime) {
            return true;
        }
        return new Date().getTime() > parseInt(expiredTime);
    }

    getMetadata() {
        const metadata = new GrpcMetadata();
        const token = this.storageService.getItem('token');
        if (token) {
            metadata.set('Authorization', `Bearer ${token}`);
        }
        return metadata;
    }

    hasAdminAccess(): boolean {
        try {
            const user = this.currentUserInfo;

            if (!user) {
                const message =
                    'Failed to load user info. Please sign in again.';

                this.errorMessage.set(message);
                this.showError(message);

                return false;
            }

            const groups = Array.isArray(user.userGroups)
                ? user.userGroups
                : [];

            const hasAccess = groups.some(
                (group: { title?: string }) =>
                    !!group?.title &&
                    this.allowedAdminUserGroups.has(
                        group.title.trim()
                    )
            );

            if (!hasAccess) {
                const message =
                    'Permission denied. You do not have access to this application.';

                this.errorMessage.set(message);
                this.showError(message);
            }

            return hasAccess;
        } catch {
            const message =
                'Unexpected error while checking permissions.';

            this.errorMessage.set(message);
            this.showError(message);

            return false;
        }
    }

    private showError(message: string): void {
        this.snackBar.open(message, 'Close', {
            duration: 6000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['snackbar-error']
        });
    }
    private getLoginErrorMessage(error: GrpcStatusEvent): string {
        const message = (error.statusMessage || '').toLowerCase();

        if (error.statusCode === 14) {
            return 'No connection. Please check your internet connection.';
        }

        if (
            message.includes('failed to fetch') ||
            message.includes('network') ||
            message.includes('no connection')
        ) {
            return 'No connection. Please check your internet connection.';
        }

        if (error.statusCode === 16) {
            return 'Invalid username or password.';
        }

        if (error.statusCode === 7) {
            return 'You do not have permission to perform this action.';
        }

        return 'Something went wrong. Please try again.';
    }

    private logoutWithReason(message: string): void {
        sessionStorage.setItem(this.logoutReasonKey, message);
        this.logOut();
    }
}
